import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import { 
  Users, FileText, Send, Check, X, Download, 
  Edit3, AlertCircle, Clock, CheckCircle, Mail, UserPlus, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enhanced Signature Pad Component - Smoother lines
const SignaturePad = ({ onSave, onCancel, userName, title = "Signature" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size for better resolution
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    
    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Smooth line settings
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const point = getCoordinates(e);
    setIsDrawing(true);
    setLastPoint(point);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const currentPoint = getCoordinates(e);
    
    if (lastPoint) {
      // Use quadratic curve for smoother lines
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      
      // Midpoint for smoother curve
      const midX = (lastPoint.x + currentPoint.x) / 2;
      const midY = (lastPoint.y + currentPoint.y) / 2;
      
      ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midX, midY);
      ctx.stroke();
    }
    
    setLastPoint(currentPoint);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setLastPoint(null);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    // Export at higher quality
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    onSave(dataUrl);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl">
        <h3 className="text-lg font-semibold mb-2 flex items-center space-x-2 text-gray-800">
          <Edit3 size={20} className="text-[#00D3A8]" />
          <span>{title}</span>
        </h3>
        
        {userName && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600">Signataire :</p>
            <p className="font-semibold text-gray-800">{userName}</p>
            <p className="text-xs text-gray-500 mt-1">
              Horodatage : {new Date().toLocaleString('fr-FR')}
            </p>
          </div>
        )}
        
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-inner">
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: '180px', touchAction: 'none' }}
            className="cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        
        <p className="text-sm text-gray-500 mb-4 text-center">
          Signez dans le cadre ci-dessus avec votre souris ou votre doigt
        </p>
        
        <div className="flex space-x-3">
          <button
            onClick={clearCanvas}
            className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Effacer
          </button>
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={saveSignature}
            className="flex-1 py-2.5 px-4 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors font-medium"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Delegation Form Component
const DelegationForm = ({ assembleeId, token, user, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [delegationType, setDelegationType] = useState('external'); // 'coproprietaire' or 'external'
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [selectedCopro, setSelectedCopro] = useState(null);
  const [loadingCopros, setLoadingCopros] = useState(false);
  const [formData, setFormData] = useState({
    mandant_civilite: user?.civilite || 'M.',
    mandant_adresse: user?.adresse || '',
    mandant_cp: user?.code_postal || '',
    mandant_ville: user?.ville || '',
    mandataire_civilite: 'M.',
    mandataire_nom: '',
    mandataire_prenom: '',
    mandataire_email: '',
    mandataire_adresse: '',
    mandataire_cp: '',
    mandataire_ville: '',
    mandataire_is_coproprietaire: false,
    mandataire_user_id: null
  });
  const [delegation, setDelegation] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load coproprietaires when component mounts
  useEffect(() => {
    const fetchCoproprietaires = async () => {
      setLoadingCopros(true);
      try {
        const response = await axios.get(
          `${API}/assemblees/${assembleeId}/coproprietaires`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCoproprietaires(response.data || []);
      } catch (err) {
        console.error('Error fetching coproprietaires:', err);
      } finally {
        setLoadingCopros(false);
      }
    };
    
    if (assembleeId && token) {
      fetchCoproprietaires();
    }
  }, [assembleeId, token]);

  // Handle coproprietaire selection
  const handleCoproSelection = (copro) => {
    setSelectedCopro(copro);
    setFormData(prev => ({
      ...prev,
      mandataire_civilite: copro.civilite || 'M.',
      mandataire_nom: copro.nom || '',
      mandataire_prenom: copro.prenom || '',
      mandataire_email: copro.email || '',
      mandataire_adresse: copro.adresse || '',
      mandataire_cp: copro.code_postal || '',
      mandataire_ville: copro.ville || '',
      mandataire_is_coproprietaire: true,
      mandataire_user_id: copro.id
    }));
  };

  // Handle delegation type change
  const handleDelegationTypeChange = (type) => {
    setDelegationType(type);
    setSelectedCopro(null);
    // Reset mandataire fields when switching type
    setFormData(prev => ({
      ...prev,
      mandataire_civilite: 'M.',
      mandataire_nom: '',
      mandataire_prenom: '',
      mandataire_email: '',
      mandataire_adresse: '',
      mandataire_cp: '',
      mandataire_ville: '',
      mandataire_is_coproprietaire: type === 'coproprietaire',
      mandataire_user_id: null
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const createDelegation = async () => {
    // Validation based on delegation type
    if (delegationType === 'coproprietaire') {
      if (!selectedCopro) {
        setError('Veuillez sélectionner un coproprietaire comme mandataire');
        return;
      }
    } else {
      if (!formData.mandataire_nom || !formData.mandataire_prenom || !formData.mandataire_email) {
        setError('Veuillez remplir tous les champs obligatoires du mandataire');
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/assemblees/${assembleeId}/delegations`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDelegation(response.data);
      setStep(2);
      
      // Show success toast with email confirmation
      const mandataireNom = `${formData.mandataire_prenom} ${formData.mandataire_nom}`.trim();
      const mandataireEmail = formData.mandataire_email;
      
      if (response.data.email_sent) {
        toast.success(
          <div className="flex flex-col">
            <span className="font-semibold">✉️ Email envoyé avec succès</span>
            <span className="text-sm text-gray-600 mt-1">
              {mandataireNom} ({mandataireEmail}) a été notifié(e) de votre délégation.
            </span>
          </div>,
          {
            duration: 5000,
            icon: <Mail className="text-green-500" size={20} />
          }
        );
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la délégation');
    } finally {
      setLoading(false);
    }
  };

  const signDelegation = async (signature) => {
    setLoading(true);
    setShowSignature(false);
    
    try {
      await axios.post(
        `${API}/delegations/${delegation.delegation_id}/sign-mandant`,
        { signature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setStep(3);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/delegations/${delegation.delegation_id}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pouvoir_delegation_${user?.nom || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erreur lors du téléchargement du PDF');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users size={28} />
              <div>
                <h2 className="text-xl font-bold">Délégation de vote</h2>
                <p className="text-sm opacity-90">Donnez pouvoir à un mandataire</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
              <X size={24} />
            </button>
          </div>
          
          {/* Progress steps */}
          <div className="flex items-center justify-between mt-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-white text-[#00D3A8]' : 'bg-white/30 text-white'
                }`}>
                  {step > s ? <Check size={18} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-20 h-1 mx-2 ${step > s ? 'bg-white' : 'bg-white/30'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span>Informations</span>
            <span>Signature</span>
            <span>Envoi</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Form */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Mandant info (readonly) */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                  <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <span>Mandant (vous)</span>
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium">{user?.prenom} {user?.nom}</p>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                </div>
              </div>

              {/* Delegation Type Selector */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                  <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <span>Type de mandataire</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => handleDelegationTypeChange('coproprietaire')}
                    data-testid="delegation-type-copro"
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      delegationType === 'coproprietaire'
                        ? 'border-[#00D3A8] bg-[#00D3A8]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        delegationType === 'coproprietaire' ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Users size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Copropriétaire</p>
                        <p className="text-xs text-gray-500">De l'immeuble</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleDelegationTypeChange('external')}
                    data-testid="delegation-type-external"
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      delegationType === 'external'
                        ? 'border-[#00D3A8] bg-[#00D3A8]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        delegationType === 'external' ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <UserPlus size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Personne externe</p>
                        <p className="text-xs text-gray-500">Hors copropriété</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Mandataire selection - Copropriétaire */}
              {delegationType === 'coproprietaire' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                    <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm">3</span>
                    <span>Choisir un coproprietaire</span>
                  </h3>
                  
                  {loadingCopros ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#00D3A8] border-t-transparent"></div>
                    </div>
                  ) : coproprietaires.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <AlertCircle className="mx-auto text-yellow-500 mb-2" size={24} />
                      <p className="text-sm text-yellow-700">
                        Aucun autre coproprietaire disponible dans cet immeuble.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleDelegationTypeChange('external')}
                        className="mt-2 text-sm text-[#00D3A8] hover:underline"
                      >
                        Déléguer à une personne externe
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                      {coproprietaires.map((copro) => (
                        <button
                          key={copro.id}
                          type="button"
                          onClick={() => handleCoproSelection(copro)}
                          data-testid={`copro-select-${copro.id}`}
                          className={`w-full p-3 rounded-lg text-left transition-all flex items-center justify-between ${
                            selectedCopro?.id === copro.id
                              ? 'bg-[#00D3A8]/10 border-2 border-[#00D3A8]'
                              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                              selectedCopro?.id === copro.id ? 'bg-[#00D3A8]' : 'bg-gray-400'
                            }`}>
                              {(copro.prenom?.[0] || '').toUpperCase()}{(copro.nom?.[0] || '').toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">
                                {copro.civilite} {copro.prenom} {copro.nom}
                              </p>
                              <p className="text-sm text-gray-500">{copro.email}</p>
                              {copro.tantiemes > 0 && (
                                <p className="text-xs text-gray-400">{copro.tantiemes} tantièmes</p>
                              )}
                            </div>
                          </div>
                          {selectedCopro?.id === copro.id && (
                            <Check className="text-[#00D3A8]" size={20} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {selectedCopro && (
                    <div className="mt-4 bg-[#00D3A8]/5 border border-[#00D3A8]/30 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Mandataire sélectionné :</p>
                      <p className="font-semibold text-gray-800">
                        {selectedCopro.civilite} {selectedCopro.prenom} {selectedCopro.nom}
                      </p>
                      <p className="text-sm text-gray-500">{selectedCopro.email}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Mandataire info - External Person */}
              {delegationType === 'external' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                    <span className="w-6 h-6 bg-[#00D3A8] text-white rounded-full flex items-center justify-center text-sm">3</span>
                    <span>Mandataire (représentant)</span>
                  </h3>
                
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Civilité</label>
                      <select
                        name="mandataire_civilite"
                        value={formData.mandataire_civilite}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                      >
                        <option value="M.">M.</option>
                        <option value="Mme">Mme</option>
                        <option value="Mlle">Mlle</option>
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-sm text-gray-600 mb-1">Prénom *</label>
                      <input
                        type="text"
                        name="mandataire_prenom"
                        value={formData.mandataire_prenom}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                        placeholder="Prénom du mandataire"
                        required
                      />
                    </div>
                  </div>
                
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Nom *</label>
                      <input
                        type="text"
                        name="mandataire_nom"
                        value={formData.mandataire_nom}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                        placeholder="Nom du mandataire"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Email *</label>
                      <input
                        type="email"
                        name="mandataire_email"
                        value={formData.mandataire_email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                        placeholder="email@exemple.com"
                        required
                      />
                    </div>
                  </div>
                
                  <div className="mb-3">
                    <label className="block text-sm text-gray-600 mb-1">Adresse</label>
                    <input
                      type="text"
                      name="mandataire_adresse"
                      value={formData.mandataire_adresse}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                      placeholder="Adresse du mandataire"
                    />
                  </div>
                
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Code postal</label>
                      <input
                        type="text"
                        name="mandataire_cp"
                        value={formData.mandataire_cp}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                        placeholder="75000"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">Ville</label>
                      <input
                        type="text"
                        name="mandataire_ville"
                        value={formData.mandataire_ville}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D3A8]"
                        placeholder="Ville"
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={createDelegation}
                disabled={loading || (delegationType === 'coproprietaire' && !selectedCopro) || (delegationType === 'external' && (!formData.mandataire_nom || !formData.mandataire_prenom || !formData.mandataire_email))}
                data-testid="delegation-continue-btn"
                className="w-full py-3 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <span>Continuer</span>
                    <Check size={18} />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Signature */}
          {step === 2 && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-[#00D3A8]/10 rounded-full flex items-center justify-center mx-auto">
                <Edit3 size={32} className="text-[#00D3A8]" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Signez le pouvoir</h3>
                <p className="text-gray-600">
                  Cliquez ci-dessous pour signer électroniquement le pouvoir de délégation.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <p className="text-sm text-gray-600 mb-1">Délégation à :</p>
                <p className="font-medium">{formData.mandataire_prenom} {formData.mandataire_nom}</p>
                <p className="text-sm text-gray-500">{formData.mandataire_email}</p>
              </div>
              
              <button
                onClick={() => setShowSignature(true)}
                className="w-full py-3 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] font-medium flex items-center justify-center space-x-2"
              >
                <Edit3 size={18} />
                <span>Signer le pouvoir</span>
              </button>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Pouvoir envoyé !</h3>
                <p className="text-gray-600">
                  Un email a été envoyé à <strong>{formData.mandataire_email}</strong> avec le pouvoir à signer.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 text-left">
                <div className="flex items-start space-x-3">
                  <Mail className="text-blue-500 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Prochaine étape</p>
                    <p className="text-sm text-blue-600">
                      Le mandataire doit ouvrir l'email et signer le pouvoir pour finaliser la délégation.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={downloadPDF}
                  className="flex-1 py-3 border border-[#00D3A8] text-[#00D3A8] rounded-lg hover:bg-[#00D3A8]/5 font-medium flex items-center justify-center space-x-2"
                >
                  <Download size={18} />
                  <span>Télécharger le PDF</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] font-medium"
                >
                  Terminer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Signature Modal */}
        {showSignature && (
          <SignaturePad
            onSave={signDelegation}
            onCancel={() => setShowSignature(false)}
            userName={`${user?.prenom} ${user?.nom}`}
            title="Signer le pouvoir de délégation"
          />
        )}
      </div>
    </div>
  );
};

export { DelegationForm, SignaturePad };
export default DelegationForm;
